import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import type { SerializedDirectedVideo } from 'fym-shared/src/music24/directedVideoApi';
import type { Music24Capabilities } from 'fym-shared/src/music24/music24Plans';
import { openSMLUpgradePrompt } from '../../../redux/slices/appInfo/slice';
import { SMLUpgradeFeature } from '../../music24/config/music24PlanCapabilities';
import { useMusic24Capabilities } from '../../music24/hooks/useMusic24Capabilities';
import { openDirectedVideoOf } from '../directedSteps';
import { useGetDirectedVideosQuery } from '../directedVideoApi';
import { directedVideoPath } from '../directedVideoPath';

/** The song facts that the entry needs: a ready master and lyrics (design a1). */
export interface DirectedEntrySong {
  hasMaster: boolean;
  masterReady: boolean;
  hasLyrics: boolean;
}

/**
 * True when the song offers a directed video (design a1): the workspace has a plan with full-song
 * videos (`canRenderFullSongVideos`, the gate of every directed video route; no access flag since
 * 2026-10-20), and the song has its audio and its lyrics. Otherwise the entry is not there at all:
 * no lock, no tease. Pure.
 */
export function offersDirectedVideo(
  capabilities: Pick<Music24Capabilities, 'canRenderFullSongVideos'>,
  song: DirectedEntrySong
): boolean {
  return capabilities.canRenderFullSongVideos && song.hasMaster && song.masterReady && song.hasLyrics;
}

/**
 * True when the plan makes no AI shots (`canMakeAiFootage`: Free and the trial). The entry still
 * shows on the trial, but a new video opens the plan offer, not the wizard (QA 2026-10-28,
 * skpr2-1): the trial has "no AI clips", and credit packs are for paid plans only. Pure.
 */
export function directedVideoNeedsPlan(capabilities: Pick<Music24Capabilities, 'canMakeAiFootage'>): boolean {
  return !capabilities.canMakeAiFootage;
}

/**
 * The directed video entry of a song (design a1, a2, a4): whether it shows, the open video of the
 * song (still edited or being made, `GET .../directed`), and the move into the wizard. The list is
 * read again on mount and on focus, so the label ("Continue", "See the progress" or the new video
 * button) is not stale. The wizard link never has a video uuid: the wizard opener reads a fresh
 * list and opens the open video, or makes a new one (owner report 2026-10-19: a cached list opened
 * a video that was ready).
 */
export function useDirectedVideoEntry(input: { accountId: number; catalogItemUuid: string; song: DirectedEntrySong }) {
  const { accountId, catalogItemUuid, song } = input;
  const capabilities = useMusic24Capabilities();
  const visible = offersDirectedVideo(capabilities, song);
  const needsPlan = directedVideoNeedsPlan(capabilities);
  const dispatch = useDispatch();
  const list = useGetDirectedVideosQuery(
    { accountId, catalogItemUuid },
    { skip: !visible, refetchOnMountOrArgChange: true, refetchOnFocus: true }
  );
  const open: SerializedDirectedVideo | null = openDirectedVideoOf(list.data?.videos ?? []);
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const start = useCallback(() => {
    // A video that the plan cannot make opens the plan offer. An open video still opens (to see it).
    if (needsPlan && open === null) {
      dispatch(openSMLUpgradePrompt({ feature: SMLUpgradeFeature.aiFootage }));
      return;
    }
    // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
    void navigate(directedVideoPath(catalogItemUuid, null, { accountId, from: `${pathname}${search}` }));
  }, [accountId, catalogItemUuid, dispatch, navigate, needsPlan, open, pathname, search]);

  return { visible, open, start, needsPlan };
}
